import { IAbortController } from '../../../infra/httpClient/AbortController';
import { IHttpClient } from '../../../infra';
import {
    GetSuggestionsRequest,
    GetSuggestionsResponse,
    ListConversationsRequest,
    ListConversationsResponse,
    CreateConversationResponse,
    CreateRunRequest,
    CreateRunResponse,
    ListConversationRunsRequest,
    ListConversationRunsResponse,
    GetRunRequest,
    GetRunResponse,
    SendConversationFeedbackRequest,
    SendRunFeedbackRequest,
    GetConversationRequest,
    GetConversationResponse,
    GenerateConversationTitleRequest,
    GenerateConversationTitleResponse,
    DeleteConversationRequest,
    DeleteConversationResponse,
} from './AssistantDTOs';

export interface BaseOptions {
    abortController?: IAbortController;
}

export interface IPlacerAssistantClient {
    getSuggestions(
        request: GetSuggestionsRequest,
        options: BaseOptions,
    ): Promise<GetSuggestionsResponse>;
    listConversations(request: ListConversationsRequest): Promise<ListConversationsResponse>;
    createConversation(): Promise<CreateConversationResponse>;
    createRun(request: CreateRunRequest): Promise<CreateRunResponse>;
    listConversationRuns(
        request: ListConversationRunsRequest,
    ): Promise<ListConversationRunsResponse>;
    getRun(request: GetRunRequest): Promise<GetRunResponse>;
    sendConversationFeedback(request: SendConversationFeedbackRequest): Promise<void>;
    sendRunFeedback(request: SendRunFeedbackRequest): Promise<void>;
    getConversation(request: GetConversationRequest): Promise<GetConversationResponse>;
    generateConversationTitle(request: GenerateConversationTitleRequest): Promise<GenerateConversationTitleResponse>;
    deleteConversation(request: DeleteConversationRequest): Promise<DeleteConversationResponse>;
}

export class PlacerAssistantClient implements IPlacerAssistantClient {
    constructor(private client: IHttpClient) {}

    async generateConversationTitle(request: GenerateConversationTitleRequest): Promise<GenerateConversationTitleResponse> {
        const { conversationId } = request;
        const response = await this.client.post<GenerateConversationTitleResponse>(
            `/v1/conversations/${conversationId}/generate_title`,
            {},
        );

        return response.data;
    }

    async getConversation(request: GetConversationRequest): Promise<GetConversationResponse> {
        const { conversationId } = request;
        const response = await this.client.get<GetConversationResponse>(
            `/v1/conversations/${conversationId}`,
        );

        return response.data;
    }

    async deleteConversation(request: GetConversationRequest): Promise<DeleteConversationResponse> {
        const { conversationId } = request;
        const response = await this.client.delete<GetConversationResponse>(
            `/v1/conversations/${conversationId}`,
        );

        return response.data;
    }

    async createConversation(): Promise<CreateConversationResponse> {
        const response = await this.client.post<CreateConversationResponse>(
            '/v1/conversations',
            {},
        );

        return response.data;
    }

    async createRun(request: CreateRunRequest): Promise<CreateRunResponse> {
        const { conversationId, content, screenshotInfo } = request;

        const result = await this.client.post<CreateRunResponse>(
            `/v1/conversations/${conversationId}/runs`,
            {
                content,
                screenshot_info: screenshotInfo,
            },
        );

        return result.data;
    }

    async listConversationRuns(
        request: ListConversationRunsRequest,
    ): Promise<ListConversationRunsResponse> {
        const { conversationId } = request;
        const response = await this.client.get<ListConversationRunsResponse>(
            `/v1/conversations/${conversationId}/runs`,
        );

        return response.data;
    }

    async getRun(request: GetRunRequest): Promise<GetRunResponse> {
        const { conversationId, runId } = request;
        const response = await this.client.get<GetRunResponse>(
            `/v1/conversations/${conversationId}/runs/${runId}`,
        );

        return response.data;
    }

    async sendConversationFeedback(request: SendConversationFeedbackRequest): Promise<void> {
        const { conversationId, feedback, score } = request;
        await this.client.post(`/v1/conversations/${conversationId}/feedback`, {
            feedback,
            score,
        });
    }

    async sendRunFeedback(request: SendRunFeedbackRequest): Promise<void> {
        const { conversationId, runId, feedback, score } = request;
        await this.client.post(
            `/v1/conversations/${conversationId}/feedback`,
            {
                feedback,
                score,
            },
            {
                params: { runId },
            },
        );
    }

    async listConversations(request: ListConversationsRequest): Promise<ListConversationsResponse> {
        const response = await this.client.get<ListConversationsResponse>('/v1/conversations', {
            params: request.paging,
        });

        return response.data;
    }

    async getSuggestions(
        request: GetSuggestionsRequest,
        options: BaseOptions,
    ): Promise<GetSuggestionsResponse> {
        const { query, maxResults, session } = request;
        const { abortController } = options;

        const response = await this.client.get<GetSuggestionsResponse>('/v1/suggest', {
            params: {
                query,
                max_results: maxResults,
                session,
            },
            abortController,
        });

        return response.data;
    }
}
